import { useParams, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { openCheckout } from '../services/nativeBrowser';
import { useEffect, useState, useContext, useCallback, useRef } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db } from '../firebase';
import { UserContext } from '../contexts/UserContext';
import { MembershipContext } from '../contexts/MembershipContext';
import axios from 'axios';
import { useContact } from '../contexts/ContactContext';
import { getNetCashReceived, userNeedsPayoutOnboarding } from '../utils/tradeUtils';
import AddressRequiredModal from '../components/AddressRequiredModal';
import PayoutAccountLinkedModal from '../components/PayoutAccountLinkedModal';
import SavingsBanner from '../components/SavingsBanner';
import { marketplaceFeeSavingsDollars } from '../utils/marketplaceSavings';

const SellerPurchaseConfirm = () => {
  const { offerId } = useParams();
  const navigate = useNavigate();
  const { firestoreUser, loading: userDataLoading, refreshUserData } = useContext(UserContext);
  const { getTradeCompletionFee, getTradeProtectFee } = useContext(MembershipContext);
  const { openContact } = useContact();
  
  const [offerData, setOfferData] = useState(null);
  const [showAddressModal, setShowAddressModal] = useState(false);
  // { reconnected, onContinue } — set on the alreadyOnboarded branch so the
  // user learns WHY Stripe onboarding never appeared; navigation to settlement
  // runs when they dismiss (navigating immediately would unmount the modal).
  const [payoutLinkedNotice, setPayoutLinkedNotice] = useState(null);
  const [targetPedal, setTargetPedal] = useState(null);

  // The seller's address is required for the Shippo label, but this page had
  // no prompt at all — a seller without one hit blank label fields later, or
  // had to detour through Profile. Auto-open the same inline modal the offer
  // pages use so they can fill it in place (found during Android testing,
  // 2026-08-20).
  const isAddressComplete = (address) => {
    if (!address) return false;
    const required = ['name', 'street1', 'city', 'state', 'zip', 'country'];
    return required.every(field => address[field] && address[field].trim());
  };
  useEffect(() => {
    if (!userDataLoading && !isAddressComplete(firestoreUser?.shippingAddress)) {
      setShowAddressModal(true);
    } else {
      setShowAddressModal(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firestoreUser, userDataLoading]);
  const [buyerUserData, setBuyerUserData] = useState({ username: '', badge: '', membershipTier: '', photoURL: '', averageRating: 0 });
  const [sellerUserData, setSellerUserData] = useState({ username: '', badge: '', membershipTier: '', photoURL: '', averageRating: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [processingPayment, setProcessingPayment] = useState(false);
  const [includeProtection, setIncludeProtection] = useState(false);
  const [applyCashCredit, setApplyCashCredit] = useState(true);
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  // One-shot guard for the default-ON persist (see the rehydrate effect).
  const defaultProtectionAttemptedRef = useRef(false);
  // A human toggle during the in-flight default write must win over the
  // default's late completion (see TradeConfirm's twin for the race).
  const userToggledProtectionRef = useRef(false);
  // False until the rehydrate/default effect has resolved the toggle's initial
  // position. The switch renders WITHOUT its slide transition until then, so
  // the default-ON state appears in place instead of visibly flicking on a
  // beat after page load (Jason, 2026-09-18).
  const [protectionHydrated, setProtectionHydrated] = useState(false);

  const handleProtectionToggle = useCallback(async (checked) => {
    if (!offerData || !auth.currentUser) return;
    // SELLER CHECK FIRST (Jason's spec, 2026-09-18): only the seller may
    // write protection.seller. The rules treat the buyer as a purchase
    // party, so without this a buyer opening the seller URL could write the
    // seller's choice. Non-sellers are read-only and write nothing.
    if (auth.currentUser.uid !== offerData.responderId) return;
    // Frozen once fees are paid: the charge was priced from the choice at
    // payment time (sellerPaymentStatus.protectionSelected, which settlement
    // prefers over this doc flag) — toggling after paying either shows
    // coverage that was never paid for, or hides coverage that was. The
    // switch renders disabled in this state; this is the backstop.
    const paid = !!(offerData.sellerPaymentStatus?.paid || offerData.paymentStatus?.sellerPaid);
    if (paid) return;
    userToggledProtectionRef.current = true;
    setIncludeProtection(checked);
    try {
      const offerRef = doc(db, 'purchaseOffers', offerData.id);
      await updateDoc(offerRef, {
        'protection.seller': checked,
        'protectionDefaulted.seller': false, // explicit choice now
        updatedAt: new Date()
      });
      setOfferData((prev) => prev ? {
        ...prev,
        protection: { ...(prev.protection || {}), seller: checked }
      } : prev);
      console.log('✅ Protection status saved to database:', checked);
    } catch (err) {
      // The doc flag is what the server settles from — never display a state
      // that failed to persist, in either direction (2026-09-18).
      console.error('❌ Error updating protection status:', err);
      setIncludeProtection(!checked);
      alert('Could not save your TradeProtect choice — please try the toggle again.');
    }
  }, [offerData]);

  // Rehydrate + default-ON, mirroring TradeConfirm (single source of truth =
  // the persisted record; saved false is sacred; default persisted before
  // shown, fail-safe OFF; never fires once paid — the payment-time record
  // wins then). Full contract: TRADEPROTECT_REPRICE_PLAN.md.
  useEffect(() => {
    if (!offerData || !auth.currentUser) return;
    // SELLER CHECK FIRST (Jason's spec, 2026-09-18): identity verified before
    // any write is scheduled. The buyer (a rules-recognized purchase party)
    // or an admin landing on this URL sees the Unauthorized render — but this
    // effect fires before render guards, so without this check their page
    // view persisted `protection.seller: true` on the seller's behalf.
    // Non-sellers: display-only state, ZERO writes (proven by the buyer test
    // in tradeProtectDefaultOn.test.js).
    if (auth.currentUser.uid !== offerData.responderId) {
      setIncludeProtection(offerData.protection?.seller === true);
      setProtectionHydrated(true);
      return;
    }
    const sps = offerData.sellerPaymentStatus || {};
    const paid = !!(sps.paid || offerData.paymentStatus?.sellerPaid);
    if (paid) {
      // Mirror what settlement will charge: the payment-time record first.
      setIncludeProtection(
        typeof sps.protectionSelected === 'boolean'
          ? sps.protectionSelected
          : offerData.protection?.seller === true
      );
      setProtectionHydrated(true);
      return;
    }
    const saved = offerData.protection?.seller;
    if (typeof saved === 'boolean') {
      setIncludeProtection(saved);
      setProtectionHydrated(true);
      return;
    }
    if (defaultProtectionAttemptedRef.current) {
      setProtectionHydrated(true);
      return;
    }
    defaultProtectionAttemptedRef.current = true;
    (async () => {
      try {
        await updateDoc(doc(db, 'purchaseOffers', offerData.id), {
          'protection.seller': true,
          // Analytics marker: defaulted, not chosen (cleared on any explicit
          // toggle). Read by no money path.
          'protectionDefaulted.seller': true,
          updatedAt: new Date()
        });
        setOfferData((prev) => prev ? {
          ...prev,
          protection: { ...(prev.protection || {}), seller: true }
        } : prev);
        if (!userToggledProtectionRef.current) setIncludeProtection(true);
      } catch (err) {
        console.error('❌ Could not persist the default TradeProtect choice (leaving it OFF):', err);
      } finally {
        setProtectionHydrated(true);
      }
    })();
  }, [offerData]);

  const getBadge = (count) => {
    if (count === undefined || count === null || isNaN(count)) return '0';
    return count.toString();
  };

  const getMembershipBadge = (tier) => {
    if (!tier) return null;
    
    const config = {
      'pro': { label: 'PRO', bgColor: '#FFC107', textColor: '#000' },
      'premium': { label: 'PREMIUM', bgColor: '#9C27B0', textColor: '#FFF' },
      'basic': { label: 'BASIC', bgColor: '#4CAF50', textColor: '#FFF' }
    };
    
    if (!config[tier]) return null;
    
    return (
      <div
        style={{
          display: 'inline-block',
          padding: '2px 6px',
          borderRadius: '4px',
          fontSize: '10px',
          fontWeight: 'bold',
          backgroundColor: config[tier].bgColor,
          color: config[tier].textColor,
          marginLeft: '6px',
          verticalAlign: 'middle'
        }}
      >
        {config[tier].label}
      </div>
    );
  };

  const getMembershipProfileBadge = (tier) => {
    if (!tier) return null;
    
    const config = {
      'pro': { label: 'PRO MEMBER', bgColor: '#FFC107', textColor: '#000' },
      'premium': { label: 'PREMIUM MEMBER', bgColor: '#9C27B0', textColor: '#FFF' },
      'basic': { label: 'BASIC MEMBER', bgColor: '#4CAF50', textColor: '#FFF' }
    };
    
    if (!config[tier]) return null;
    
    return (
      <div
        style={{
          display: 'inline-block',
          padding: '4px 12px',
          borderRadius: '999px',
          fontSize: '12px',
          fontWeight: 'bold',
          backgroundColor: config[tier].bgColor,
          color: config[tier].textColor,
          marginLeft: '8px'
        }}
      >
        {config[tier].label}
      </div>
    );
  };

  const renderStars = (rating, size = '16px') => {
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    return (
      <div style={{ display: 'inline-flex', gap: '2px' }}>
        {[...Array(fullStars)].map((_, i) => (
          <span key={`full-${i}`} style={{ color: '#FFC107', fontSize: size }}>★</span>
        ))}
        {hasHalfStar && <span style={{ color: '#FFC107', fontSize: size }}>⯪</span>}
        {[...Array(emptyStars)].map((_, i) => (
          <span key={`empty-${i}`} style={{ color: '#E0E0E0', fontSize: size }}>★</span>
        ))}
      </div>
    );
  };

  // Check if user is returning from Stripe onboarding and should go to settlement instead.
  // Net-gated (mirrors TradeConfirm): only auto-advance if the seller will actually NET
  // cash — a net-zero seller shouldn't be pushed into settlement just for returning.
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const isOnboardingReturn = urlParams.has('onboarding') || urlParams.has('stripe_return');
    if (!isOnboardingReturn || !offerData || !auth.currentUser) return;

    const agreed = Number(offerData.acceptedAmount || offerData.offerAmount) || 0;
    let lf = getTradeCompletionFee();
    if (lf > 0 && (firestoreUser?.referralCredits || 0) > 0) lf = 0;
    // Price from the persisted record, not local toggle state — on a fresh
    // onboarding return this can run before the rehydrate effect. `undefined`
    // counts as protected: that's what the default-ON persist writes.
    const sps = offerData.sellerPaymentStatus || {};
    const docProtected = typeof sps.protectionSelected === 'boolean'
      ? sps.protectionSelected
      : offerData.protection?.seller !== false;
    const fees = lf + (docProtected ? getTradeProtectFee() : 0);
    const net = getNetCashReceived({ grossReceived: agreed, totalFees: fees, applyCashCredit });
    if (net > 0) {
      console.log('🔄 User returned from Stripe onboarding (nets cash), redirecting to settlement...');
      navigate(`/seller-purchase-settlement/${offerId}?onboarding=return`);
    }
  }, [offerData, navigate, offerId, firestoreUser, includeProtection, applyCashCredit, getTradeCompletionFee, getTradeProtectFee]);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        // Fetch purchase offer data
        const offerRef = doc(db, 'purchaseOffers', offerId);
        const offerSnap = await getDoc(offerRef);
        
        if (!offerSnap.exists()) {
          // Auth is already resolved here (loadData only runs for a logged-in
          // user), so a missing doc is a genuine not-found — never a login flash.
          setError('Purchase offer not found.');
          setLoading(false);
          return;
        }

        const offer = { id: offerSnap.id, ...offerSnap.data() };
        setOfferData(offer);

        // Fetch target pedal data
        const pedalRef = doc(db, 'listings', offer.targetPedalId);
        const pedalSnap = await getDoc(pedalRef);
        
        if (!pedalSnap.exists()) {
          setError('Pedal not found.');
          setLoading(false);
          return;
        }

        setTargetPedal({ id: pedalSnap.id, ...pedalSnap.data() });

        // Fetch buyer (initiator) user data
        const buyerRef = doc(db, 'publicProfiles', offer.initiatorId);
        const buyerSnap = await getDoc(buyerRef);
        if (buyerSnap.exists()) {
          const buyerData = buyerSnap.data();
          setBuyerUserData({
            username: buyerData.username || buyerData.displayName || 'Unknown',
            badge: getBadge(buyerData.completedTrades || 0),
            membershipTier: buyerData.membershipTier || null,
            photoURL: buyerData.photoURL || '/images/default-avatar.png',
            averageRating: buyerData.averageRating || 0
          });
        }

        // Fetch seller (responder) user data
        const sellerRef = doc(db, 'publicProfiles', offer.responderId);
        const sellerSnap = await getDoc(sellerRef);
        if (sellerSnap.exists()) {
          const sellerData = sellerSnap.data();
          setSellerUserData({
            username: sellerData.username || sellerData.displayName || 'Unknown',
            badge: getBadge(sellerData.completedTrades || 0),
            membershipTier: sellerData.membershipTier || null,
            photoURL: sellerData.photoURL || '/images/default-avatar.png',
            averageRating: sellerData.averageRating || 0
          });
        }

        // Protection state is owned by the rehydrate/default-ON effect above.

        setLoading(false);
      } catch (err) {
        console.error('Error loading purchase data:', err);
        setError('Failed to load purchase data.');
        setLoading(false);
      }
    };

    // Wait for auth to resolve before deciding. On a fresh load / pull-to-refresh
    // reload, auth.currentUser is briefly null; driving off onAuthStateChanged
    // keeps a logged-in user on the loading state instead of flashing a login /
    // not-found message.
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        sessionStorage.setItem('returnAfterLogin', window.location.pathname);
        setError('Please log in to view this purchase. Click here to log in: /login');
        setLoading(false);
        return;
      }
      loadData();
    });

    return () => unsubscribe();
  }, [offerId]);

  const handleStripeCheckout = async () => {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('You must be logged in to complete payment.');
      }

      console.log('🔐 Getting fresh auth token...');
      const idToken = await user.getIdToken(true);
      console.log('✅ Auth token refreshed');

      // Use the same backend endpoint as trades, but for purchase offer fees
      // The backend will treat this similar to a trade where the seller owes fees
      const checkoutData = {
        tradeId: offerId, // Using offerId - backend will look this up in purchaseOffers
        userId: user.uid,
        returnTo: Capacitor.isNativePlatform() ? 'app' : undefined,
        protectionSelected: includeProtection,
        applyCashCredit: applyCashCredit,
        appliedCredit: applyCashCredit ? Math.min(agreedAmount, totalFees) : 0,
        isPurchaseSellerPayment: true, // Flag to indicate this is seller paying fees for a purchase
        metadata: {
          type: 'purchase_seller_payment',
          offerId: offerId,
          targetPedalId: offerData.targetPedalId,
          buyerId: offerData.initiatorId,
          sellerId: offerData.responderId
        }
      };

      console.log('💳 Creating Stripe checkout for seller purchase fees:', checkoutData);
      
      const baseUrl = 'https://us-central1-pedalxchange.cloudfunctions.net';
      
      const response = await fetch(`${baseUrl}/createStripeCheckout`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify(checkoutData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      
      console.log('📦 Stripe checkout result:', result);
      
      // Handle zero-payment paths (cash credit or referral credit covers everything)
      if (result.noPaymentRequired) {
        console.log('✅ No Stripe payment needed — redirecting');
        navigate(result.url || `/seller-purchase-settlement/${offerId}`);
        return;
      }

      if (result.alreadyPaid) {
        // Server F2 guard: fees already recorded — go to settlement instead
        // of erroring (or consuming a second referral credit).
        console.log('ℹ️ Seller fees already paid — redirecting to settlement');
        navigate(`/seller-purchase-settlement/${offerId}`);
        return;
      }

      if (result.url || result.checkoutUrl) {
        console.log('✅ Checkout session created, redirecting to Stripe...');
        // Native only (web uses Stripe's own success/cancel URLs). `onReturn`
        // fires on ANY sheet dismissal — including after CANCELLING the
        // checkout. Navigating straight to settlement let a cancelled seller
        // tap "Settle Sale & Continue" with the fee never collected (the
        // net-zero sellers routed through this path have no payout gate to
        // stop them). So poll for the webhook's sellerPaid write and only
        // advance when payment is confirmed; otherwise stay here so they can
        // retry. Mirrors the onboarding onReturn re-verification above.
        await openCheckout(result.url || result.checkoutUrl, {
          onReturn: () => {
            let attempts = 0;
            const maxAttempts = 6;
            const poll = async () => {
              attempts += 1;
              try {
                const snap = await getDoc(doc(db, 'purchaseOffers', offerId));
                const data = snap.exists() ? snap.data() : null;
                const paid = !!(data?.sellerPaymentStatus?.paid || data?.paymentStatus?.sellerPaid);
                if (paid) {
                  navigate(`/seller-purchase-settlement/${offerId}`);
                  return;
                }
              } catch (err) {
                console.warn('Post-checkout seller payment check failed:', err);
              }
              if (attempts < maxAttempts) {
                setTimeout(poll, 2000);
              } else {
                console.log('ℹ️ Seller fee payment not confirmed after sheet closed — staying on confirm');
                setProcessingPayment(false);
              }
            };
            poll();
          }
        });
      } else {
        throw new Error(result.error || 'Failed to create checkout session');
      }
    } catch (err) {
      console.error('❌ Error creating Stripe checkout:', err);
      setError(`Failed to create checkout: ${err.message}`);
      setProcessingPayment(false);
    }
  };

  const handleProceedToPayment = async () => {
    setProcessingPayment(true);
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('No logged-in user.');

      // 🔒 NET-BASED ONBOARDING GATE — runs BEFORE the referral/payment branch.
      // Previously the `finalAmount > 0 || hasReferralCredit` branch ran first, so a
      // seller whose fees were covered by a referral credit was sent straight to the
      // referral checkout (which marks them paid) and never set up a payout account —
      // even though they will pocket real money. Gate on NET received cash first, off
      // the SAME `netCashReceived`/`needsPayoutSetup` that drives the UI below.
      if (netCashReceived > 0) {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const onboarded = userDoc.exists() && !!userDoc.data().stripeOnboardingComplete;
        if (!onboarded) {
          console.log('🔄 Seller will net cash but payout onboarding incomplete — redirecting to onboarding');
          await redirectToStripeOnboarding();
          return;
        }
        // Onboarded net receiver → settlement. The seller must NEVER go through the
        // payment/referral checkout: their fee is netted from the sale proceeds, so
        // routing here would wrongly consume a referral credit and mark them "paid"
        // via the payer flow, dumping them on progress instead of settlement.
        console.log('✅ Net receiver onboarded — proceeding to settlement');
        navigate(`/seller-purchase-settlement/${offerId}`);
        return;
      }

      // NOT a net receiver (edge: fees exceed received cash) → checkout / referral path.
      if (finalAmount > 0 || hasReferralCredit) {
        console.log('💳 Seller payment: $' + finalAmount.toFixed(2) + (hasReferralCredit ? ' (referral credit applied)' : '') + ' - redirecting to checkout');
        await handleStripeCheckout();
        return;
      }

      // Nothing owed and any required payout account is verified → settlement.
      console.log('✅ All checks passed - proceeding to settlement');
      navigate(`/seller-purchase-settlement/${offerId}`);
    } catch (err) {
      console.error('Error proceeding to payment:', err);
      setError('Failed to proceed to payment.');
      setProcessingPayment(false);
    }
  };

  const redirectToStripeOnboarding = async () => {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('No logged-in user.');
      const idToken = await user.getIdToken();
      
      // Set up return URL to go directly to settlement page after wallet creation
      const returnUrl = `${window.location.origin}/seller-purchase-settlement/${offerId}?onboarding=return`;
      
      const response = await axios.post(
        'https://us-central1-pedalxchange.cloudfunctions.net/createStripeConnectAccount',
        { 
          userId: user.uid,
          returnUrl: returnUrl,
          returnTo: Capacitor.isNativePlatform() ? 'app' : undefined
        },
        { headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        } }
      );

      if (response.data.alreadyOnboarded) {
        // Stripe says already onboarded (Firestore flag was stale). Go to
        // settlement WITH ?onboarding=return so it runs its authoritative Stripe
        // recheck instead of trusting a stale payoutReady=false (which would
        // briefly flash the "Set up payout" gate for an onboarded seller).
        console.log('User already has Stripe account:', response.data.stripeAccountId);
        setPayoutLinkedNotice({
          reconnected: !!response.data.reconnected,
          onContinue: () => navigate(`/seller-purchase-settlement/${offerId}?onboarding=return`),
        });
      } else if (response.data.accountLinkUrl) {
        // Native only (onReturn never fires on web): re-attach the stripeAccountId
        // the function just returned so SellerPurchaseSettlement's reconcile effect
        // calls checkStripeAccountStatus and persists the account. On web Stripe's
        // own return_url already carries it. The account.updated webhook is the
        // server-side backstop if the user never taps back in.
        const acctId = response.data.stripeAccountId;
        const settlementUrl = acctId
          ? `/seller-purchase-settlement/${offerId}?onboarding=return&stripeAccountId=${acctId}`
          : `/seller-purchase-settlement/${offerId}?onboarding=return`;
        // onReturn fires on ANY sheet dismissal — including tapping the X without
        // finishing. Re-verify with Stripe and only advance to settlement if onboarding
        // actually completed; otherwise stay on this page so the seller can retry.
        // Native-only (web uses Stripe's own return_url). Mirrors TradeConfirm.
        await openCheckout(response.data.accountLinkUrl, {
          onReturn: async () => {
            // POLL, don't check once: Stripe can flip `transfers` to active a few
            // seconds after onboarding finishes, so an immediate single check sees
            // "not ready" and strands the seller here. Mirrors TradeConfirm.
            let complete = false;
            for (let attempt = 0; attempt < 6 && !complete; attempt++) {
              if (attempt > 0) await new Promise(r => setTimeout(r, 2500));
              try {
                await axios.post(
                  'https://us-central1-pedalxchange.cloudfunctions.net/checkStripeAccountStatus',
                  { userId: user.uid, stripeAccountId: acctId || undefined },
                  { headers: { Authorization: `Bearer ${idToken}` } }
                );
                const fresh = await getDoc(doc(db, 'users', user.uid));
                complete = fresh.exists() && !!fresh.data().stripeOnboardingComplete;
              } catch (e) {
                console.warn('Payout status re-check failed after onboarding sheet closed:', e?.message || e);
              }
            }
            if (refreshUserData) { try { await refreshUserData(); } catch { /* non-fatal */ } }
            if (complete) {
              navigate(settlementUrl);
            } else {
              // Stay-put path: re-enable the button. handleProceedToPayment set
              // processingPayment=true with no finally, so without this a seller
              // who dismissed onboarding unfinished was stuck on "Processing…".
              setProcessingPayment(false);
            }
          }
        });
      } else {
        throw new Error(response.data.error || 'Failed to create Stripe account');
      }
    } catch (err) {
      console.error('Error creating Stripe account:', err);
      setError('Failed to initiate payment setup.');
      setProcessingPayment(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4" style={{ backgroundColor: '#F7F3EB', minHeight: 'calc(100vh - var(--navbar-height, 125px))' }}>
        <div className="text-center">Loading purchase confirmation...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4" style={{ backgroundColor: '#F7F3EB', minHeight: 'calc(100vh - var(--navbar-height, 125px))' }}>
        <div className="text-center text-red-600">{error}</div>
        <div className="text-center mt-4">
          <button 
            onClick={() => navigate(-1)}
            className="text-blue-600 hover:underline"
          >
            ← Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!offerData || !targetPedal) {
    return (
      <div className="p-4" style={{ backgroundColor: '#F7F3EB', minHeight: 'calc(100vh - var(--navbar-height, 125px))' }}>
        <div className="text-center text-red-600">Purchase data not found.</div>
      </div>
    );
  }

  const userId = auth.currentUser?.uid;
  const isSeller = userId === offerData.responderId;

  if (!isSeller) {
    return (
      <div className="p-4" style={{ backgroundColor: '#F7F3EB', minHeight: 'calc(100vh - var(--navbar-height, 125px))' }}>
        <div className="text-center text-red-600">Unauthorized access.</div>
      </div>
    );
  }

  const agreedAmount = Number(offerData.acceptedAmount || offerData.offerAmount) || 0;
  let listingFee = getTradeCompletionFee();
  
  // Check for referral credit — waive listing fee if seller has credits
  const hasReferralCredit = listingFee > 0 && (firestoreUser?.referralCredits || 0) > 0;
  if (hasReferralCredit) {
    listingFee = 0;
  }
  
  const sellerAlreadyPaid = !!(offerData?.sellerPaymentStatus?.paid || offerData?.paymentStatus?.sellerPaid);
  const protectionFee = includeProtection ? getTradeProtectFee() : 0;
  const totalFees = listingFee + protectionFee;
  const receivedCash = agreedAmount;
  const appliedCredit = applyCashCredit ? Math.min(receivedCash, totalFees) : 0;
  const finalAmount = totalFees - appliedCredit;
  const pedalxchangeKeeps = applyCashCredit ? appliedCredit : 0;

  // Net-based payout gating (mirrors TradeConfirm). The seller only needs a payout
  // account if they will actually pocket money after fees/credit. This single value
  // drives the info panel, the button label, AND the onboarding redirect below so
  // they can never disagree.
  const netCashReceived = getNetCashReceived({ grossReceived: receivedCash, totalFees, applyCashCredit });
  const needsPayoutSetup = userNeedsPayoutOnboarding({
    netCashReceived,
    stripeOnboardingComplete: firestoreUser?.stripeOnboardingComplete,
  });

  // Savings banner: the SELLER'S avoided marketplace fee on the actual agreed
  // sale amount — gated on the responderId (this doc type's seller field), so
  // an admin or non-party opening the page never sees it. Keyed per offer +
  // user in localStorage so it can never repeat for this deal; it mounts
  // nowhere else (the progress pages deliberately do not show it).
  const isSellerParticipant = auth.currentUser?.uid === offerData?.responderId;
  const savingsDollars = marketplaceFeeSavingsDollars(agreedAmount);

  return (
    <div className="trade-confirm-outer" style={{ backgroundColor: '#F7F3EB', minHeight: 'calc(100vh - var(--navbar-height, 125px))', display: 'flex', flexDirection: 'column', padding: '1.5rem', boxSizing: 'border-box', overflowX: 'hidden' }}>
      {isSellerParticipant && (
        <SavingsBanner
          storageKey={`purchase:${offerId}:${auth.currentUser.uid}`}
          amountDollars={savingsDollars}
          variant="sale"
        />
      )}
      <style jsx>{`
        .single-card-container {
          box-sizing: border-box;
        }
        @media (max-width: 768px) {
          .trade-confirm-outer {
            padding: 0.75rem !important;
          }
          .single-card-container {
            padding: 0.75rem !important;
            border-radius: 8px !important;
            margin: 0 !important;
            max-width: 100% !important;
          }
          .trade-confirm-outer h2 {
            font-size: 1.5rem !important;
            margin-bottom: 1rem !important;
            padding: 0 0.25rem !important;
          }
          .trade-info-text {
            font-size: 0.9375rem !important;
            padding: 0 !important;
          }
          .tradeprotect-buttons {
            flex-direction: column !important;
            gap: 0.75rem !important;
          }
          .tradeprotect-buttons button {
            width: 100% !important;
            min-width: 0 !important;
            padding: 10px 16px !important;
          }
          .user-data-loading {
            margin: 0 0 1rem 0 !important;
            max-width: 100% !important;
          }
        }
      `}</style>

      <h2 className="text-2xl font-bold mb-4 text-center">Confirm Your Sale</h2>

      {userDataLoading && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded user-data-loading" style={{ maxWidth: '900px', margin: '0 auto 1rem auto', width: '100%', boxSizing: 'border-box' }}>
          <p className="text-sm text-blue-800">
            ⏳ <strong>Loading account information...</strong> Please wait while we verify your identity and payment status.
          </p>
        </div>
      )}

      <div className="single-card-container" style={{
        maxWidth: '900px',
        margin: '0 auto',
        width: '100%',
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '2rem',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        boxSizing: 'border-box'
      }}>

        {/* 1. Sale Information */}
        <div style={{ marginBottom: '2rem', paddingBottom: '2rem', borderBottom: '1px solid #e5e7eb' }}>
          <p className="text-gray-700 trade-info-text" style={{ fontSize: '1.125rem', lineHeight: '1.6', textAlign: 'center' }}>
            You are confirming the sale of your <strong>{targetPedal.make} {targetPedal.model}</strong> to <strong>{buyerUserData.username}</strong> for <strong>${agreedAmount.toFixed(2)}</strong>.
          </p>
        </div>

        {/* 2. Optional Protection */}
        <div style={{ marginBottom: '2rem', paddingBottom: '2rem', borderBottom: '1px solid #e5e7eb' }}>
          <h4 className="font-semibold mb-3" style={{ fontSize: '1.25rem', color: '#C9A227' }}>Optional Protection</h4>

          <div className="mb-3" style={{ marginBottom: '1.5rem' }}>
            <div className="font-medium mb-2">Add TradeProtect for ${getTradeProtectFee().toFixed(2)}</div>
            <p className="text-xs text-gray-600 mb-2">
              Up to $250 reimbursement for eligible fraud or misrepresentation claims, not related to shipping damage, loss, theft, or carrier delays.
            </p>
            <button
              onClick={() => setShowDisclaimerModal(true)}
              className="text-xs hover:underline"
              style={{
                // Absolute, not relative: this button has no authored size (its
                // text-xs class is inert Tailwind), and a calc(1em + 1pt) bump
                // compounded with mobile Safari's font boosting into way more
                // than 1pt. 15px = the inherited 14px + 1pt, everywhere.
                fontSize: '15px',
                color: '#5A9ECB',
                background: 'none',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                fontFamily: 'Nunito, sans-serif'
              }}
            >
              Read full disclaimer and terms
            </button>
          </div>

          {/* TradeProtect toggle — defaults ON (2026-09-18), frozen once
              the fees are paid (the payment-time record is what settlement
              charges). Replaces the Accept/Decline pair and its gate. */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem' }}>
            <button
              type="button"
              role="switch"
              aria-checked={includeProtection}
              disabled={sellerAlreadyPaid}
              onClick={() => handleProtectionToggle(!includeProtection)}
              style={{
                position: 'relative',
                width: '52px',
                height: '30px',
                borderRadius: '9999px',
                border: 'none',
                padding: 0,
                cursor: sellerAlreadyPaid ? 'not-allowed' : 'pointer',
                backgroundColor: includeProtection ? '#C9A227' : '#D1D5DB',
                opacity: sellerAlreadyPaid ? 0.6 : 1,
                transition: 'background-color 0.2s',
                visibility: protectionHydrated ? 'visible' : 'hidden',
                flexShrink: 0
              }}
            >
              <span style={{
                position: 'absolute',
                top: '3px',
                left: includeProtection ? '25px' : '3px',
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                backgroundColor: 'white',
                boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                transition: 'left 0.2s'
              }} />
            </button>
            <span style={{ fontFamily: 'Nunito, sans-serif', fontWeight: 700, fontSize: '15px', visibility: protectionHydrated ? 'visible' : 'hidden', color: includeProtection ? '#C9A227' : '#6B7280' }}>
              {sellerAlreadyPaid
                ? (includeProtection ? 'TradeProtect added (locked at payment)' : 'TradeProtect declined (locked at payment)')
                : (includeProtection ? 'TradeProtect added' : 'TradeProtect declined')}
            </span>
          </div>
        </div>

        {/* 3. Payment Breakdown */}
        <div style={{ marginBottom: '2rem', background: '#fafafa', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '1.25rem', fontFamily: 'Nunito' }}>
          <h4 style={{ fontSize: '1.1rem', fontWeight: '700', color: '#1f2937', marginBottom: '1rem' }}>Payment breakdown</h4>

          <div style={{ fontSize: '0.7rem', fontWeight: '700', color: '#9ca3af', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: '0.5rem' }}>Fees</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem', fontSize: '0.9rem' }}>
            <span style={{ color: '#374151' }}>Platform fee</span>
            <span style={{ fontWeight: '600', color: '#374151' }}>
              {hasReferralCredit ? (
                <span style={{ color: '#22c55e' }}>$0.00 <span style={{ fontSize: '0.75rem' }}>(Referral credit)</span></span>
              ) : (
                `$${listingFee.toFixed(2)}`
              )}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem', fontSize: '0.9rem' }}>
            <span style={{ color: '#374151' }}>TradeProtect fee</span>
            <span style={{ fontWeight: '600', color: '#374151' }}>${protectionFee.toFixed(2)}</span>
          </div>

          {receivedCash > 0 && (
            <>
              <div style={{ borderTop: '1px solid #e5e7eb', margin: '0.75rem 0', paddingTop: '0.75rem' }}>
                <div style={{ fontSize: '0.7rem', fontWeight: '700', color: '#9ca3af', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: '0.5rem' }}>Cash you're receiving</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem', fontSize: '0.9rem' }}>
                  <span style={{ color: '#374151' }}>Cash from buyer</span>
                  <span style={{ fontWeight: '600', color: '#15803d' }}>${receivedCash.toFixed(2)}</span>
                </div>
              </div>

              {totalFees > 0 && (
                <>
                  <div style={{ background: '#f5f5f4', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '0.75rem', marginBottom: '0.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer', gap: '0.75rem' }} onClick={() => setApplyCashCredit(!applyCashCredit)}>
                      <div style={{
                        width: '44px', minWidth: '44px', height: '24px', borderRadius: '12px',
                        backgroundColor: applyCashCredit ? '#5A9ECB' : '#d1d5db',
                        position: 'relative', transition: 'background-color 0.2s', marginTop: '2px'
                      }}>
                        <div style={{
                          width: '20px', height: '20px', borderRadius: '50%', backgroundColor: 'white',
                          position: 'absolute', top: '2px', left: applyCashCredit ? '22px' : '2px',
                          transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                        }} />
                      </div>
                      <div style={{ textAlign: 'left' }}>
                        <span style={{ fontWeight: '600', fontSize: '0.85rem', color: '#1f2937' }}>Apply cash towards fees</span>
                        <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '2px' }}>
                          PedalxChange keeps the applied amount, you receive the remainder.
                        </p>
                      </div>
                    </div>
                  </div>

                  {applyCashCredit && (
                    <div style={{ paddingLeft: '0.25rem' }}>
                      {appliedCredit > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem', fontSize: '0.85rem' }}>
                          <span style={{ color: '#6b7280' }}>Applied to fees</span>
                          <span style={{ fontWeight: '600', color: '#15803d' }}>-${appliedCredit.toFixed(2)}</span>
                        </div>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem', fontSize: '0.85rem' }}>
                        <span style={{ color: '#6b7280' }}>Total you'll receive</span>
                        <span style={{ fontWeight: '600', color: '#15803d' }}>${(receivedCash - appliedCredit).toFixed(2)}</span>
                      </div>
                    </div>
                  )}
                </>
              )}

              {totalFees === 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem', fontSize: '0.85rem' }}>
                  <span style={{ color: '#6b7280' }}>Total you'll receive</span>
                  <span style={{ fontWeight: '600', color: '#15803d' }}>${receivedCash.toFixed(2)}</span>
                </div>
              )}
            </>
          )}

          <div style={{ borderTop: '2px solid #d1d5db', marginTop: '0.75rem', paddingTop: '0.75rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: '700', fontSize: '1rem', color: '#1f2937' }}>Final amount due</span>
            <span style={{ fontWeight: '700', fontSize: '1.1rem', color: finalAmount > 0 ? '#b91c1c' : '#15803d' }}>
              ${finalAmount.toFixed(2)}
            </span>
          </div>
        </div>

        {/* 4. Payment Button */}
        <div>
          {finalAmount > 0 ? (
            <>
              {needsPayoutSetup && (
                <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '10px', padding: '14px 16px', marginBottom: '1rem', textAlign: 'left', maxWidth: '420px', marginLeft: 'auto', marginRight: 'auto' }}>
                  <p style={{ margin: 0, fontSize: '0.9rem', color: '#9a3412', lineHeight: '1.5' }}>
                    <strong>You're receiving ${receivedCash.toFixed(2)} from this sale.</strong> To get paid, you'll set up a one-time, free, secure payout account with Stripe.
                  </p>
                </div>
              )}
            <div className="flex justify-center">
              <button
                onClick={handleProceedToPayment}
                disabled={processingPayment || userDataLoading || !protectionHydrated}
                className="hover:opacity-80 transition-opacity"
                style={{
                  backgroundColor: (processingPayment || userDataLoading || !protectionHydrated) ? '#9CA3AF' : '#C9A227',
                  color: (processingPayment || userDataLoading || !protectionHydrated) ? '#E5E7EB' : 'white',
                  border: 'none',
                  borderRadius: '9999px',
                  padding: '12px 24px',
                  fontFamily: 'Nunito, sans-serif',
                  fontWeight: 'bold',
                  fontSize: '16px',
                  cursor: (processingPayment || userDataLoading || !protectionHydrated) ? 'not-allowed' : 'pointer',
                  maxWidth: '300px',
                  width: '100%'
                }}
              >
                {processingPayment ? 'Processing Payment...' :
                 userDataLoading ? 'Loading account information...' :
                 needsPayoutSetup ? `Set up payout to receive $${receivedCash.toFixed(2)}` :
                 `Proceed to Payment ($${finalAmount.toFixed(2)})`}
              </button>
            </div>
            </>
          ) : (
            <div className="text-center">
              <div style={{ background: '#e8f5f0', border: '1px solid #b2dfdb', borderRadius: '8px', padding: '0.75rem', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <div style={{ width: '22px', height: '22px', borderRadius: '50%', backgroundColor: '#4db6ac', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <span style={{ fontSize: '0.85rem', color: '#2e7d6e' }}>
                  <strong>No payment required!</strong>{' '}
                  {receivedCash > 0
                    ? "You'll receive cash when the buyer pays."
                    : "You don't owe any money for this sale."}
                </span>
              </div>
              {needsPayoutSetup && (
                <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '10px', padding: '14px 16px', marginBottom: '1rem', textAlign: 'left', maxWidth: '420px', marginLeft: 'auto', marginRight: 'auto' }}>
                  <p style={{ margin: 0, fontSize: '0.9rem', color: '#9a3412', lineHeight: '1.5' }}>
                    <strong>You're receiving ${receivedCash.toFixed(2)} from this sale.</strong> To get paid, you'll set up a one-time, free, secure payout account with Stripe.
                  </p>
                </div>
              )}
              <button
                onClick={handleProceedToPayment}
                disabled={processingPayment || userDataLoading || !protectionHydrated}
                className="hover:opacity-80 transition-opacity"
                style={{
                  backgroundColor: (processingPayment || userDataLoading || !protectionHydrated) ? '#9CA3AF' : '#C9A227',
                  color: (processingPayment || userDataLoading || !protectionHydrated) ? '#E5E7EB' : 'white',
                  border: 'none',
                  borderRadius: '9999px',
                  padding: '12px 24px',
                  fontFamily: 'Nunito, sans-serif',
                  fontWeight: 'bold',
                  fontSize: '16px',
                  cursor: (processingPayment || userDataLoading || !protectionHydrated) ? 'not-allowed' : 'pointer',
                  maxWidth: '300px',
                  width: '100%'
                }}
              >
                {processingPayment ? 'Processing...' :
                 userDataLoading ? 'Loading account information...' :
                 needsPayoutSetup ? `Set up payout to receive $${receivedCash.toFixed(2)}` :
                 'Continue to Sale Settlement'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Disclaimer Modal */}
      {showDisclaimerModal && (
        <div
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem'
          }}
          onClick={() => setShowDisclaimerModal(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '2rem',
              paddingBottom: '3rem',
              maxWidth: '600px',
              maxHeight: '80vh',
              overflowY: 'auto',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-bold text-xl mb-4" style={{ color: '#C9A227', textAlign: 'left' }}>Important Disclaimer & Terms</h3>

            <div className="space-y-4" style={{ fontSize: '0.875rem', lineHeight: '1.6', color: '#4A5759', textAlign: 'left' }}>
              <p>
                You are selling directly to another user, not to PedalxChange.com, LLC ("PedalxChange"). Online trading involves inherent risks, including fraud, misrepresentation, theft, damage, shipping issues, and other problems that may result in financial loss.
              </p>

              <div>
                <h4 className="font-semibold mb-2" style={{ color: '#C9A227' }}>Nature of TradeProtect</h4>
                <ul className="ml-4 space-y-2" style={{ listStyleType: 'disc' }}>
                  <li>TradeProtect is an optional good-will reimbursement program offered by PedalxChange for certain proven fraudulent or fraudulently misrepresented trades or transactions only.</li>
                  <li>TradeProtect is not an insurance policy, does not create an insurance contract, and does not make PedalxChange an insurer or insurance producer.</li>
                  <li>TradeProtect does not cover shipping damage, wear and tear, buyer's remorse, preference changes, normal transaction risk, or any non-fraud-related issues.</li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold mb-2" style={{ color: '#C9A227' }}>Claims, Investigation, and Discretion</h4>
                <ul className="ml-4 space-y-2" style={{ listStyleType: 'disc' }}>
                  <li>TradeProtect may provide up to $250 in good-will reimbursement per eligible incident in PedalxChange's sole and absolute discretion, after review of the claim and supporting evidence.</li>
                  <li>All claims are subject to investigation, and PedalxChange may request cooperation, documentation, communications, photos, police reports, and any other evidence it deems necessary.</li>
                  <li>PedalxChange does not guarantee approval of any claim or any particular reimbursement amount; claims may be approved in full, approved in part, or denied entirely, for any reason or no stated reason.</li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold mb-2" style={{ color: '#C9A227' }}>User Responsibilities and Conduct</h4>
                <ul className="ml-4 space-y-2" style={{ listStyleType: 'disc' }}>
                  <li>You are expected to act in good faith, comply with our Terms of Service, and ship the exact item listed, in the described condition, within the agreed timeframe.</li>
                  <li>Users who send broken, incorrect, counterfeit, or fraudulent gear, or who attempt to defraud other users or PedalxChange, may have their accounts suspended or terminated and may be subject to further legal action.</li>
                  <li>You are responsible for complying with all applicable laws, including any tax, customs, and reporting obligations related to your sales.</li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold mb-2" style={{ color: '#C9A227' }}>Limitation of Liability and Release</h4>
                <p className="mb-2">
                  PedalxChange is an online platform that facilitates connections between users but is not a party to your trades.
                </p>
                <p className="mb-2">
                  To the maximum extent permitted by law, PedalxChange and its owners, officers, employees, and agents are not responsible or liable for:
                </p>
                <ul className="ml-4 space-y-2 mb-3" style={{ listStyleType: 'disc' }}>
                  <li>The outcome of any trade or transaction between users.</li>
                  <li>Any fraud, theft, misrepresentation, or deception by users.</li>
                  <li>Any lost, stolen, delayed, damaged, or destroyed property, gear, or money (including shipping issues, even where separate shipping coverage exists).</li>
                  <li>Any payment disputes, chargebacks, failed payments, or reversals.</li>
                  <li>Any indirect, incidental, consequential, special, or punitive damages, including lost profits, loss of use, or loss of goodwill, arising from or related to use of the platform or any trade.</li>
                </ul>
                <p className="mb-2 font-semibold">
                  By proceeding with payment, you acknowledge and agree that:
                </p>
                <ol className="ml-4 space-y-2" style={{ listStyleType: 'decimal' }}>
                  <li>You are selling directly, and at your own risk, to another user and not to PedalxChange.</li>
                  <li>PedalxChange may provide safety and verification tools but cannot prevent all fraud or misconduct.</li>
                  <li>Any TradeProtect reimbursement is not guaranteed, is not insurance, and is provided, if at all, purely as a discretionary good‑will payment by PedalxChange.</li>
                  <li>PedalxChange is not liable for trade outcomes, disputes, or any losses you may incur in connection with your use of the platform.</li>
                  <li>You release and agree to hold harmless and not sue PedalxChange (and its owners, officers, employees, and agents) from any and all claims, demands, causes of action, damages, losses, costs, and expenses arising out of or related to your trades, except to the limited extent such release is prohibited by applicable law.</li>
                </ol>
              </div>

              <p className="text-xs text-gray-500 mt-4">
                For full terms and conditions governing your use of PedalxChange and TradeProtect, please review our Terms of Service and Privacy Policy, which are incorporated by reference into this disclaimer.
              </p>
            </div>

            <div className="mt-6 flex justify-center" style={{ marginBottom: '4rem' }}>
              <button
                onClick={() => setShowDisclaimerModal(false)}
                className="hover:opacity-80 transition-opacity"
                style={{
                  backgroundColor: '#C9A227',
                  color: 'white',
                  border: 'none',
                  borderRadius: '9999px',
                  padding: '10px 24px',
                  fontFamily: 'Nunito, sans-serif',
                  fontWeight: 'bold',
                  fontSize: '15px',
                  cursor: 'pointer'
                }}
              >
                I Understand
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fixed gap below content */}
      <div style={{ height: '60px' }}></div>

      {/* Footer */}
      <div className="py-4 trade-confirm-footer" style={{ paddingBottom: '10px', backgroundColor: '#F7F3EB' }}>
        <style jsx>{`
          @media (max-width: 768px) {
            .trade-confirm-footer {
              padding-bottom: 80px !important;
            }
          }
        `}</style>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-sm text-gray-600">
              Need help or found a bug?{' '}
              <button
                onClick={() => openContact({ category: 'support' })}
                className="hover:underline"
                style={{
                  color: '#5A9ECB',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  font: 'inherit',
                  cursor: 'pointer'
                }}
              >
                Contact PXC Support
              </button>
            </p>
          </div>
        </div>
      </div>

      {/* Inline shipping-address modal — auto-opens when the seller has no
          complete address on file (needed for the Shippo label). */}
      <AddressRequiredModal
        isOpen={showAddressModal}
        onClose={() => setShowAddressModal(false)}
        onSaved={() => setShowAddressModal(false)}
        user={firestoreUser}
        context="shipping"
      />

      <PayoutAccountLinkedModal
        isOpen={!!payoutLinkedNotice}
        reconnected={payoutLinkedNotice?.reconnected}
        onClose={() => {
          const next = payoutLinkedNotice?.onContinue;
          setPayoutLinkedNotice(null);
          if (next) next();
        }}
      />
    </div>
  );
};

export default SellerPurchaseConfirm;
